<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $ } from '@bagelink/vue'
const defaults = {
	previewState: 'form',
	formName: 'contact',
	title: 'Contact Us',
	description: '<p>We would love to hear from you.</p>',
	successMessage: '<h3>Thank you!</h3><p>Your message has been sent.</p>',
	errorMessage: 'Something went wrong. Please try again.',
}
const schema = defineSchema({
	previewState: $.select('Preview State', [
		{ label: 'Form', value: 'form' },
		{ label: 'Submitting', value: 'submitting' },
		{ label: 'Success', value: 'success' },
		{ label: 'Error', value: 'error' },
	]),
	formName: $.text('Form Name'),
	title: $.text('Title'),
	description: $.richtext('Description', { basic: true }),
	successMessage: $.richtext('Success Message', { basic: true }),
	errorMessage: $.text('Error Message'),
})
export const BlockContact = defineBlock({
	thumbnail: '/block-thumbs/BlockContact.svg',
	category: 'CTA',
	order: 190,
	name: 'BlockContact',
	label: 'Contact Form',
	icon: 'mail',
	schema,
	defaults,
})
</script>
<script setup lang="ts">

import { Btn, EmailInput, TextInput } from '@bagelink/vue'
import { computed, ref } from 'vue'
import { form } from '@shared/api'




const props = defineProps<{
	previewState?: 'form' | 'submitting' | 'success' | 'error'
	formName?: string
	title?: string
	description?: string
	successMessage?: string
	errorMessage?: string
}>()

// Form data
const formData = ref({
	firstName: '',
	lastName: '',
	email: '',
	message: '',
})

// Form status
const formStatus = ref('') // '', 'success', 'error'
const submitting = ref(false)
const visibleStatus = computed(() =>
	isDesignerPreview() && props.previewState && props.previewState !== 'form'
		? props.previewState
		: formStatus.value,
)
const isSubmitting = computed(() => submitting.value || visibleStatus.value === 'submitting')
const isDesignerPreview = () =>
	typeof window !== 'undefined' && window.location.pathname.startsWith('/_blox_preview')

async function handleSubmit() {
	if (submitting.value) return

	submitting.value = true
	formStatus.value = ''

	const callbacks = {
		onSuccess: () => {
			formStatus.value = 'success'
			formData.value = {
				firstName: '',
				lastName: '',
				email: '',
				message: '',
			}
			submitting.value = false
		},
		onError: () => {
			formStatus.value = 'error'
			submitting.value = false
		},
		onComplete: () => {
			submitting.value = false
		},
	}

	if (isDesignerPreview()) {
		return
	}

	try {
		await form.submit.postByFormName({
			formName: props.formName || 'contact',
			formSubmissionCreate: {
				_meta: {},
				form_name: props.formName || 'contact',
				form_data: { ...formData.value },
				form_complete: true,
				page_url: typeof window === 'undefined' ? undefined : window.location.href,
			},
		})
		callbacks.onSuccess()
	} catch {
		callbacks.onError()
	}
}
</script>

<template>
	<div class="w720px mx-auto m_w-100p py-3 m_px-1 m_py-1-75 pageHeight contactWrap">
		<h1 class="mt-0 txt40 regular line-height-14 m_txt32 m_mt-05 m_txt-center">
			{{ title }}
		</h1>

		<div class="txt17 m_txt15" v-html="description" />

		<div
			v-if="visibleStatus === 'success'"
			class="success-message txt-center bg-gray py-3 px-1 txt17"
			v-html="successMessage"
		/>

		<form v-else class="grid grid-wrap-2 py-1 gap-col-1" @submit.prevent="handleSubmit">
			<!-- Name Fields Row -->
			<TextInput
				v-model="formData.firstName"
				placeholder="First Name"
				class="form-input"
				:disabled="isSubmitting"
				required
			/>
			<TextInput
				v-model="formData.lastName"
				placeholder="Last Name"
				class="form-input"
				:disabled="isSubmitting"
				required
			/>
			<!-- Email Field -->
			<EmailInput
				v-model="formData.email"
				placeholder="Email"
				:disabled="isSubmitting"
				required
				class="grid-span-2 m_grid-span-1"
			/>

			<!-- Message Field -->
			<TextInput
				v-model="formData.message"
				placeholder="Message"
				rows="6"
				:disabled="isSubmitting"
				required
				class="grid-span-2 m_grid-span-1"
			/>

			<div class="flex space-between grid-span-2 m_grid-span-1 align-items-start m_block">
				<!-- Submit Button -->
				<Btn type="submit" class="m_mt-1 ms-auto" :disabled="isSubmitting" full-width-mobile>
					{{ isSubmitting ? 'Submitting...' : 'Submit' }}
				</Btn>
			</div>
			<!-- Error Message -->
			<div
				v-if="visibleStatus === 'error'"
				class="bg-red color-white txt14 mt-1 p-05 txt-center w-100p grid-span-2 m_grid-span-1"
			>
				{{ errorMessage }}
			</div>
		</form>
	</div>
</template>

<style>
.success-message h3 {
	font-size: 30px;
	margin-block: 0.5rem;
}
.contactWrap label span {
	display: none;
}
</style>
